<template>
    <div>
        <sui-segment :loading="isLoading">
            <h1 is="sui-header">{{ $t('gateway.config.title') }}</h1>

            <sui-form v-if="!isLoading" @submit="onSaveGatewayFile">
                <h2 is="sui-header">{{ $t('gateway.config.commonTitle') }}</h2>

                <sui-form-fields :fields="isUpdate ? 'three' : 'two'">
                    <sui-form-field v-if="isUpdate">
                        <label>{{ $t('common.id') }}</label>
                        <input type="text" :value="activeCustomerGateway.id" disabled>
                    </sui-form-field>

                    <sui-form-field :required="!isUpdate">
                        <label>{{ $t('gateway.config.software') }}</label>
                        <input v-if="isUpdate" type="text" :value="activeCustomerGateway.gateway.name" disabled>
                        <sui-dropdown
                            v-else
                            :placeholder="$t('gateway.config.software')"
                            selection
                            search
                            :options="setGatewaysOptions"
                            @input="setGateway"
                            v-model="activeCustomerGateway.gateway.id"
                        >
                        </sui-dropdown>
                    </sui-form-field>

                    <sui-form-field>
                        <label>{{ $t('common.customer') }}</label>
                        <input type="text" :value="activeCustomerGateway.createdBy.name" disabled>
                    </sui-form-field>
                </sui-form-fields>

                <sui-form-field required>
                    <label>{{ $t('common.name') }}</label>
                    <input type="text" required v-model="activeCustomerGateway.name" placeholder="Code agence 202_2615, ...">
                </sui-form-field>

                <sui-form-field>
                    <label>{{ $t('gateway.globalConfig.fileEncoding') }}</label>
                    <sui-dropdown
                        :placeholder="$t('gateway.globalConfig.fileEncodingPlaceholder')"
                        selection
                        :options="setEncodingOptions"
                        v-model="activeCustomerGateway.encodingId"
                        required
                    >
                    </sui-dropdown>
                </sui-form-field>

                <sui-form-field>
                    <label>{{ $t('gateway.config.countMediasToDownload') }}</label>
                    <sui-list bulleted>
                        <sui-list-item><b>{{ $t('gateway.config.empty') }}</b> = {{ $t('gateway.config.emptyDesc') }}</sui-list-item>
                        <sui-list-item><b>0</b> = {{ $t('common.unlimited') }}</sui-list-item>
                    </sui-list>
                    <input type="number" step="1" min="0" max="999" v-model="activeCustomerGateway.maxMediaDownloadCount">
                </sui-form-field>

                <sui-form-field>
                    <label>{{ $t('gateway.config.downloadBatchLength') }}</label>
                    <input type="number" step="1" min="0" max="100" v-model="activeCustomerGateway.downloadBatchLength">
                </sui-form-field>

                <sui-form-field>
                    <label>{{ $t('common.comment') }}</label>
                    <textarea rows="1" v-model="activeCustomerGateway.comment"></textarea>
                </sui-form-field>

                <h2 is="sui-header">{{ $t('common.metadatas') }}</h2>

                <sui-form-field v-for="(metadata) in activeCustomerGateway.gateway.metadatas" :key="metadata.id" required>
                    <label>{{ metadata.code }}</label>
                    <p style="font-size: 12px" v-html="metadata.description"></p>

                    <sui-message v-if="metadata.conflicts && metadata.conflicts.length > 0" color="yellow">
                        <p>{{ $t('gateway.config.metadatasConflicts') }}</p>
                        <p>{{ $t('gateway.config.metadatasConflicts2') }}</p>
                        <sui-message-list>
                            <sui-message-item v-for="(conflict) in metadata.conflicts" :key="conflict.metadata_id">
                                (#{{ conflict.customerId }}) <b>{{ conflict.customerName }}</b><br/>

                                <router-link
                                    class="ui right labeled mini icon button"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    :to="{
                                        name: 'CustomerGatewayForm',
                                        params: {
                                            customer_id: conflict.customerId,
                                            gateway_file_id: conflict.gatewayFileId,
                                        }
                                    }"
                                >
                                    <i class="external square alternate icon"></i>(#{{ conflict.gatewayFileId }}) {{ $t('gateway.config.configGatway') }}
                                </router-link>
                            </sui-message-item>
                        </sui-message-list>
                        <p>
                        </p>
                    </sui-message>

                    <div v-if="metadata.code === '{{ZIP_NAME}}'" class="ui right labeled input">
                        <input
                            @input="setMetadataValue($event, metadata.id)"
                            :value="getMetadataValue(metadata.id)"
                            type="text"
                            placeholder="adaptimmo.zip, 4785.zip, ..."
                            required
                        />
                        <div class="ui label">.zip</div>
                    </div>
                    <input
                        v-else
                        type="text"
                        @input="setMetadataValue($event, metadata.id)"
                        :value="getMetadataValue(metadata.id)"
                        required
                    />
                </sui-form-field>

                <!--<sui-form-field v-for="(metadata, idx) in activeCustomerGateway.metadataValues" :key="metadata.id" required>
                    <label>{{ getMetadata(metadata.id).code }}</label>
                    <p style="font-size: 12px" v-html="getMetadata(metadata.id).description"></p>

                    <div v-if="getMetadata(metadata.id).code === '{{ZIP_NAME}}'" class="ui right labeled input">
                        <input
                            @input="setZipName($event, metadata.id)"
                            :value="getZipName(idx)"
                            type="text"
                            placeholder="adaptimmo.zip, 4785.zip, ..."
                            required
                        />
                        <div class="ui label">.zip</div>
                    </div>
                    <input v-else type="text" v-model="metadata.value" required/>
                </sui-form-field>-->

                <p v-if="activeCustomerGateway.gateway.metadatas.length === 0">{{ $t('gateway.config.noDataToFill') }}</p>

                <sui-button fluid color="violet" :loading="isSaving" :disabled="isSaving">{{ $t('common.save') }}</sui-button>
            </sui-form>
        </sui-segment>

        <sui-segment>
            <h1 is="sui-header">{{ $t('common.actions') }}</h1>
            <sui-button
                color="red"
                @click.native="toggleDeleteModal"
                :loading="isRemoving"
                :disabled="isRemoving || !isUpdate"
            >{{ $t('common.delete') }}</sui-button>

            <a
                v-if="activeGateway.configuration.fetchMethod === 'LOCAL_FTP'"
                :href="ftpConnectionString"
                class="ui right labeled icon button"
                target="_blank"
                rel="noopener noreferrer"
            >
                <i class="external square alternate icon"></i>{{ $t('gateway.ftp.ftpView') }}
            </a>

            <sui-modal v-model="isDeleteModalOpen">
                <sui-modal-content>
                    <sui-modal-description>
                        <p>
                            <sui-icon name="exclamation triangle" color="red"></sui-icon>
                            <b>{{ $t('gateway.config.deleteWarning') }}</b>
                        </p>
                    </sui-modal-description>
                </sui-modal-content>
                <sui-modal-actions>
                    <sui-button @click.native="toggleDeleteModal">{{ $t('common.cancel') }}</sui-button>
                    <sui-button color="red" @click.native="onDeleteGatewayFile">{{ $t('common.delete') }}</sui-button>
                </sui-modal-actions>
            </sui-modal>
        </sui-segment>
    </div>
</template>

<script>
    import store from '@/store';
    import { mapGetters } from 'vuex';

    export default {
        name: 'CustomerGatewayForm',
        beforeMount() {
            store.commit('setActiveCustomerGateway');
            store.commit('setActiveGateway');
        },
        beforeDestroy() {
            store.commit('setActiveCustomerGateway');
            store.commit('setActiveGateway');
            store.commit('removeAppTitle');
        },
        data() {
            return {
                isRemoving: false,
                isSaving: false,
                customerId: this.$route.params.customer_id,
                isLoading: false,
                isUpdate: !!this.$route.params.gateway_file_id,
                isDeleteModalOpen: false,
            }
        },
        async created() {
            this.isLoading = true;

            await store.dispatch('getCustomerById', this.$route.params.customer_id);
            await store.dispatch('getEncodings');

            if (this.isUpdate) {
                await store.dispatch('getCustomerGatewayById', {
                    customerId: this.$route.params.customer_id,
                    gatewayFileId: this.$route.params.gateway_file_id,
                });

                await store.dispatch('getGatewayById', this.activeCustomerGateway.gateway.id);
            } else {
                await store.dispatch('getGateways');

                this.activeCustomerGateway.createdBy.id = this.activeCustomer.id;
                this.activeCustomerGateway.createdBy.name = this.activeCustomer.name;

                this.setGateway(this.gateways.length > 0 ? this.gateways[0].id : -1);
            }

            this.setGatewayFileMetadata(this.activeCustomerGateway.gateway.metadatas);

            store.commit('setAppTitle', `${this.activeCustomer.name} - ${this.activeCustomerGateway.gateway.name}`);

            this.isLoading = false;
        },
        methods: {
            toggleDeleteModal() {
                this.isDeleteModalOpen = !this.isDeleteModalOpen;
            },
            getMetadata(id) {
                return this.activeCustomerGateway.gateway.metadatas.find(m => m.id === id);
            },
            setGateway(id) {
                const gateway = this.gateways.find(g => g.id === id);

                this.activeCustomerGateway.gateway.id = gateway.id;
                this.activeCustomerGateway.gateway.name = gateway.name;
                this.activeCustomerGateway.gateway.metadatas = (gateway && gateway.configuration && gateway.configuration.metadatas)
                    ? gateway.configuration.metadatas
                    : [];

                this.setGatewayFileMetadata(this.activeCustomerGateway.gateway.metadatas);
            },
            setGatewayFileMetadata(metadatas) {
                this.activeCustomerGateway.metadataValues = metadatas.map(metadata => ({
                    id: metadata.id,
                    value: this.getMetadataValue(metadata.id),
                }));
            },
            async onSaveGatewayFile(e) {
                e.preventDefault();
                this.isSaving = true;
                const hasBeenSaved = await store.dispatch('saveGatewayFile');
                this.isSaving = false;
                this.$router.push({ name: 'CustomerGatewaysList' });

                (hasBeenSaved)
                    ? this.$toastr.s(this.$t('gateway.form.saveSuccess'))
                    : this.$toastr.e(this.$t('gateway.form.saveError'));
            },
            async onDeleteGatewayFile() {
                this.isRemoving = true;
                const hasBeenRemoved = await store.dispatch('removeGatewayFile', {
                    customerId: this.$route.params.customer_id,
                    gatewayFileId: this.$route.params.gateway_file_id,
                });
                this.isRemoving = false;

                this.$router.push({ name: 'CustomerGatewaysList' });

                (hasBeenRemoved)
                    ? this.$toastr.s(this.$t('gateway.form.deleteSuccess'))
                    : this.$toastr.e(this.$t('gateway.form.deleteError'));
            },
            formatZipName(value) {
                return (value && value.endsWith('.zip')) ? value.split('.zip')[0] : value;
            },
            setMetadataValue({ target }, metadataId) {
                const metadataIdx = this.activeCustomerGateway.metadataValues.findIndex(m => m.id === metadataId);

                /*if (metadataIdx === -1) {
                    this.activeCustomerGateway.metadataValues.push({
                        id: metadataId,
                        value: null,
                    });

                    metadataIdx = this.activeCustomerGateway.metadataValues.length - 1;
                }*/

                const metadata = this.activeCustomerGateway.gateway.metadatas.find(m => m.id === metadataId);
                const isZipNameMetadata = metadata.code === '{{ZIP_NAME}}';

                const value = (isZipNameMetadata)
                    ? this.formatZipName(target.value)
                    : target.value;

                this.activeCustomerGateway.metadataValues[metadataIdx].value = value;
                return value;
            },
            getMetadataValue(metadataId) {
                const metadata = this.activeCustomerGateway.gateway.metadatas.find(m => m.id === metadataId);
                const isZipNameMetadata = metadata.code === '{{ZIP_NAME}}';
                const metadataValue = this.activeCustomerGateway.metadataValues.find(mv => mv.id === metadataId);

                if (!metadataValue) {
                    return;
                }

                return (isZipNameMetadata)
                    ? this.formatZipName(metadataValue.value)
                    : metadataValue.value;
            }
        },
        computed: {
            ...mapGetters([
                'gateways',
                'activeGateway',
                'activeCustomer',
                'activeCustomerGateway',
            ]),
            zipName: {
                get() {
                    return this.activeCustomerGateway.gateway.metadatas.find(m => m.code === '{{ZIP_NAME}}').value;
                }
            },
            ftpConnectionString: {
                get() {
                    const { login, secret } = this.activeGateway.configuration.localFTPConfiguration;
                    return `ftp://${login}:${secret}@${process.env.VUE_APP_FTP_DOMAIN}/${this.$route.params.gateway_file_id}`;
                }
            },
            setGatewaysOptions() {
                return this.gateways.map((gateway) => ({
                    text: gateway.name,
                    value: gateway.id,
                }));
            },
            setEncodingOptions() {
                return store.state.encodings.map(encoding => ({
                    text: encoding.name,
                    value: encoding.id,
                }))
            },
            pageTitle: {
                get() {
                    if (!this.activeCustomerGateway) {
                        return null;
                    }

                    const name = this.activeCustomerGateway.name ? ` - ${this.activeCustomerGateway.name} - ` : ' - ';

                    return `#${this.activeCustomerGateway.id} ${name} ${this.activeCustomerGateway.gateway.name} - ${this.activeCustomer.name}`;
                }
            },
            state: {
                get() { return store.state },
            }
        },
    }
</script>
